import type { ComponentProps } from 'react';
import { cn } from './cn';
import { focusRing } from './focus';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'border border-border bg-chrome text-fg hover:border-border-strong hover:bg-chrome-hover',
  ghost: 'text-fg hover:bg-surface-2',
  danger: 'border border-danger bg-chrome text-danger hover:bg-danger hover:text-surface',
};
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3.5 text-xs',
  md: 'h-10 gap-2 px-4 text-sm',
  lg: 'h-12 gap-2 px-5 text-sm',
};

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string): string {
  return cn(
    'inline-flex shrink-0 items-center justify-center rounded-full font-sans font-medium whitespace-nowrap os-press disabled:pointer-events-none disabled:opacity-50 pointer-coarse:min-h-11',
    focusRing,
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}

export function Button({
  variant,
  size,
  loading = false,
  className,
  children,
  type = 'button',
  disabled,
  ...rest
}: ComponentProps<'button'> & { variant?: ButtonVariant; size?: ButtonSize; loading?: boolean }) {
  return (
    <button type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonClass(variant, size, className)} {...rest}>
      {loading && <Spinner />}
      {children}
    </button>
  );
}

/** The look of an in-app text link (a `Link` that is not a button). */
export const linkClass = cn('rounded-sm font-sans text-sm text-accent underline-offset-2 hover:underline', focusRing);
